import {
  GearOutlineIcon,
  QuestionMarkOutlineIcon,
  AccountOutlineIcon,
  DataSharingOutlinedIcon,
} from "@tandemdiabetes/sugarmate-react-components"
import { Link } from "@tanstack/react-router"
import classNames from "classnames"
import {
  forwardRef,
  useCallback,
  useMemo,
} from "react"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"

import { AccountList } from "./AccountList"
import styles from "./NavigationMenu.module.scss"
import { useNavigationMenuContext } from "./useNavigationMenuContext"

import {
  useFollowedUsers,
  usePolyglot,
} from "src/contexts"
import { followToSharer } from "src/utils"

const polyglotPrefix = "components.navigation_menu."
type Props = ComponentPropsWithoutRef<"nav">

export const NavigationMenuNav = forwardRef<HTMLElement, Props>(
  function NavigationMenuNav(props: Props, ref: Ref<HTMLElement>) {
    const {
      currentUserId,
      selfFollow,
      currentFollow,
      follows,
      isFollowOnlyWithNoFollows,
    } = useFollowedUsers()

    const className = classNames(
      styles.nav,
      props.className,
    )

    const { setOpen } = useNavigationMenuContext()
    const polyglot = usePolyglot()

    const closeMenu = useCallback(
      () => {
        setOpen(false)
      },
      [setOpen],
    )

    const otherFollows = useMemo(
      () => follows.filter((follow) => !follow.is_self),
      [follows],
    )

    const displayName = isFollowOnlyWithNoFollows
      ? polyglot.t(`${polyglotPrefix}menu_item_unavailable`)
      : followToSharer(currentFollow)

    return (
      <nav
        {...props}
        className={className}
        ref={ref}
      >
        <div className={styles.usersList}>
          {selfFollow && !selfFollow.is_follow_only && (
            <AccountList
              headerTitle={polyglot.t(`${polyglotPrefix}my_account`)}
              follows={selfFollow}
              closeMenu={closeMenu}
            />
          )}
          {otherFollows.length > 0 && (
            <AccountList
              headerTitle={polyglot.t(`${polyglotPrefix}following`)}
              follows={otherFollows}
              closeMenu={closeMenu}
            />
          )}
        </div>
        <div className={styles.navigationList}>
          <Link
            to={currentFollow.is_self ? "/settings" : "/settings/$accountId"}
            params={currentFollow.is_self ? {} : { accountId: currentUserId }}
            id="settings"
            className={styles.link}
            onClick={closeMenu}
          >
            <GearOutlineIcon className={styles.linkIcon} />
            <div className={styles.usernameContainer}>
              <div className={styles.linkText}>
                {polyglot.t(`${polyglotPrefix}settings`)}
              </div>
              <div className={styles.username}>
                {displayName}
              </div>
            </div>
          </Link>
          <Link
            to="/data_sharing"
            id="data-sharing"
            className={styles.link}
            onClick={closeMenu}
          >
            <DataSharingOutlinedIcon className={styles.linkIcon} />
            <div className={styles.linkText}>
              {polyglot.t(`${polyglotPrefix}data_sharing`)}
            </div>
          </Link>
          <Link
            to="/account"
            id="my-account-btn"
            className={styles.link}
            onClick={closeMenu}
          >
            <AccountOutlineIcon className={styles.linkIcon} />
            <div className={styles.linkText}>
              {polyglot.t(`${polyglotPrefix}my_account`)}
            </div>
          </Link>
          <Link
            to="/help_and_feedback"
            id="help-and-feedback"
            className={styles.link}
            onClick={closeMenu}
          >
            <QuestionMarkOutlineIcon className={styles.linkIcon} />
            <div className={styles.linkText}>
              {polyglot.t(`${polyglotPrefix}help_and_feedback`)}
            </div>
          </Link>
        </div>
      </nav>
    )
  },
)
